import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, CalendarIcon, LinkIcon, MapPinIcon } from '@utils/icons';
import type { IGenbaSite, ITrusstorProject } from '@interfaces/IProjectMerge';
import { EnabledChip, GenbaStatusChip, LinkedChip } from './PaneState';
import styles from './SiteRow.module.css';

/* ------------------------------------------------------------------------ *
 * One selectable site row, two flavours: a Genba site (left pane) and a
 * Trusstor project (right pane). Already-linked rows render a dashed, greyed
 * variant carrying a "→ other side" note and are not selectable. Each row is
 * a real <button> so it is keyboard-operable; aria-pressed reflects selection.
 * ------------------------------------------------------------------------ */

/** Info about the row's link partner on the other side. */
export interface ILinkedInfo {
  /** Partner display name. */
  name: string;
  /** Partner stable code (Trusstor id or Genba number). */
  code: string;
}

interface IGenbaRowProps {
  site: IGenbaSite;
  selected: boolean;
  linkedInfo?: ILinkedInfo;
  onSelect: (number: string) => void;
}

export function GenbaRow({ site, selected, linkedInfo, onSelect }: IGenbaRowProps) {
  const { t } = useTranslation();
  const isLinked = !!linkedInfo;
  const cls = [styles.row, isLinked ? styles.linkedRow : styles.selectable, selected ? styles.selected : '']
    .filter(Boolean)
    .join(' ');
  return (
    <button
      type="button"
      className={cls}
      disabled={isLinked}
      aria-pressed={selected}
      onClick={() => !isLinked && onSelect(site.number)}
    >
      <span className={styles.rowMain}>
        <span className={styles.rowName}>
          <span className={`${styles.nm} ${styles.nmJp}`}>{site.name}</span>
          {site.nameEn ? <span className={styles.nmEn}>{site.nameEn}</span> : null}
        </span>
        <span className={styles.rowCode}>{site.number}</span>
      </span>
      <span className={styles.rowMeta}>
        {isLinked ? (
          <LinkedChip icon={<LinkIcon size={11} />} label={t('projectMerge.row.linked')} />
        ) : (
          <GenbaStatusChip status={site.status} statusName={site.statusName} />
        )}
        {site.address ? (
          <span className={`${styles.addr} ${styles.addrJp}`}>
            <MapPinIcon size={12} />
            {site.address}
          </span>
        ) : null}
      </span>
      {isLinked && linkedInfo ? (
        <span className={styles.linkedNote}>
          <ArrowRightIcon size={11} />
          {t('projectMerge.row.linkedToTrusstor')} <b>{linkedInfo.name}</b> · <b>{linkedInfo.code}</b>
        </span>
      ) : null}
    </button>
  );
}

interface ITrusstorRowProps {
  proj: ITrusstorProject;
  selected: boolean;
  linkedInfo?: ILinkedInfo;
  onSelect: (id: string) => void;
}

export function TrusstorRow({ proj, selected, linkedInfo, onSelect }: ITrusstorRowProps) {
  const { t } = useTranslation();
  const isLinked = !!linkedInfo;
  const selectable = !isLinked && proj.enabled;
  const cls = [
    styles.row,
    selectable ? styles.selectable : '',
    isLinked ? styles.linkedRow : '',
    !proj.enabled && !isLinked ? styles.disabled : '',
    selected ? styles.selected : '',
  ]
    .filter(Boolean)
    .join(' ');
  const locality = [proj.city, proj.country].filter(Boolean).join(', ');
  return (
    <button
      type="button"
      className={cls}
      disabled={!selectable}
      aria-pressed={selected}
      onClick={() => selectable && onSelect(proj.id)}
    >
      <span className={styles.rowMain}>
        <span className={styles.rowName}>
          <span className={styles.nm}>{proj.name}</span>
          {locality ? <span className={styles.nmEn}>{locality}</span> : null}
        </span>
        <span className={styles.rowCode}>{proj.id}</span>
      </span>
      <span className={styles.rowMeta}>
        {isLinked ? (
          <LinkedChip icon={<LinkIcon size={11} />} label={t('projectMerge.row.linked')} />
        ) : (
          <EnabledChip enabled={proj.enabled} />
        )}
        {proj.start && proj.end ? (
          <span className={styles.dates}>
            <CalendarIcon size={12} />
            {proj.start} → {proj.end}
          </span>
        ) : null}
      </span>
      {isLinked && linkedInfo ? (
        <span className={styles.linkedNote}>
          <ArrowRightIcon size={11} />
          {t('projectMerge.row.linkedToGenba')} <b>{linkedInfo.name}</b> · <b>{linkedInfo.code}</b>
        </span>
      ) : null}
    </button>
  );
}
